import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import creditCardListApi from '../../../middleware/api/ExpenseManagement/CreditCardList/creditCardListApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  CreditCardList,
  CreditCardListSliceState,
} from '../../../types/ExpenseManagement/CreditCardList/creditCardListTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'

const getCreditCardsList = createAsyncThunk(
  '/ExpenseManagement/getCardsList',
  async (_, thunkApi) => {
    try {
      return await creditCardListApi.getCreditCardsList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addCreditCardsList = createAsyncThunk(
  '/ExpenseManagement/addCardDetails',
  async (
    { cardName, cardNumber }: { cardName: string; cardNumber: string },
    thunkApi,
  ) => {
    try {
      return await creditCardListApi.addCreditCardDetails({
        cardName,
        cardNumber,
      })
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const editCreditCardDetails = createAsyncThunk(
  '/ExpenseManagement/editCardDetials',
  async (cardId: number, thunkApi) => {
    try {
      return await creditCardListApi.editCreditCardList(cardId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateCreditCardList = createAsyncThunk(
  '/ExpenseManagement/updateCardDetails',
  async (data: CreditCardList, thunkApi) => {
    try {
      return await creditCardListApi.updateCreditCardDetails(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteCreditCardList = createAsyncThunk(
  '/ExpenseManagement/deleteCardData',
  async (cardId: number, thunkApi) => {
    try {
      return await creditCardListApi.deleteCreditCardDetails(cardId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialCreditCardListState: CreditCardListSliceState = {
  getCardsList: [],
  isLoading: ApiLoadingState.idle,
  error: null,
  IsLoadingAdd: ApiLoadingState.idle,
  isLoadingDelete: ApiLoadingState.idle,
  isLoadingUpdate: ApiLoadingState.idle,
}

const creditCardListSlice = createSlice({
  name: 'credit card',
  initialState: initialCreditCardListState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addCreditCardsList.fulfilled, (state) => {
        state.IsLoadingAdd = ApiLoadingState.succeeded
      })
      .addCase(addCreditCardsList.pending, (state) => {
        state.IsLoadingAdd = ApiLoadingState.loading
      })
      .addCase(addCreditCardsList.rejected, (state) => {
        state.IsLoadingAdd = ApiLoadingState.failed
      })
      .addCase(updateCreditCardList.fulfilled, (state) => {
        state.isLoadingUpdate = ApiLoadingState.succeeded
      })
      .addCase(updateCreditCardList.pending, (state) => {
        state.isLoadingUpdate = ApiLoadingState.loading
      })
      .addCase(updateCreditCardList.rejected, (state) => {
        state.isLoadingUpdate = ApiLoadingState.failed
      })
      .addCase(deleteCreditCardList.fulfilled, (state) => {
        state.isLoadingDelete = ApiLoadingState.succeeded
      })
      .addCase(deleteCreditCardList.pending, (state) => {
        state.isLoadingDelete = ApiLoadingState.loading
      })
      .addCase(deleteCreditCardList.rejected, (state) => {
        state.isLoadingDelete = ApiLoadingState.failed
      })

      .addMatcher(
        isAnyOf(getCreditCardsList.fulfilled, addCreditCardsList.fulfilled),
        (state, action) => {
          state.isLoading = ApiLoadingState.succeeded
          state.getCardsList = action.payload
        },
      )
      .addMatcher(
        isAnyOf(
          editCreditCardDetails.fulfilled,
          updateCreditCardList.fulfilled,
          deleteCreditCardList.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          getCreditCardsList.pending,
          editCreditCardDetails.pending,
          updateCreditCardList.pending,
          deleteCreditCardList.pending,
          addCreditCardsList.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getCreditCardsList.rejected,
          editCreditCardDetails.rejected,
          updateCreditCardList.rejected,
          deleteCreditCardList.rejected,
          addCreditCardsList.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.creditCardList.isLoading
const creditCards = (state: RootState): CreditCardList[] =>
  state.creditCardList.getCardsList
const getIsLoadingAdd = (state: RootState): LoadingState =>
  state.creditCardList.IsLoadingAdd
const getIsLoadingUpdate = (state: RootState): LoadingState =>
  state.creditCardList.isLoadingUpdate
const getIsLoadingDelete = (state: RootState): LoadingState =>
  state.creditCardList.isLoadingDelete

const creditCardListThunk = {
  getCreditCardsList,
  addCreditCardsList,
  editCreditCardDetails,
  updateCreditCardList,
  deleteCreditCardList,
}

const creditCardListSelectors = {
  isLoading,
  creditCards,
  getIsLoadingAdd,
  getIsLoadingDelete,
  getIsLoadingUpdate,
}

export const creditCardListService = {
  ...creditCardListThunk,
  actions: creditCardListSlice.actions,
  selectors: creditCardListSelectors,
}

export default creditCardListSlice.reducer
